import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Activity, ArrowLeft, GitBranch, FileText, ShieldCheck, Clock,
  Layers, RefreshCw, Ban, DollarSign, Boxes, ListTree, Loader2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const runTone = (s) => ({
  completed: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
  running: "text-sky-300 border-sky-500/40 bg-sky-500/10",
  created: "text-zinc-300 border-zinc-500/40 bg-zinc-500/10",
  authorized: "text-indigo-300 border-indigo-500/40 bg-indigo-500/10",
  failed: "text-rose-300 border-rose-500/40 bg-rose-500/10",
  cancelled: "text-amber-300 border-amber-500/40 bg-amber-500/10",
}[s] || "text-zinc-400 border-zinc-600/40 bg-zinc-700/10");

const evTone = (s) => ({
  verified: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
  partial: "text-amber-300 border-amber-500/40 bg-amber-500/10",
  mixed: "text-sky-300 border-sky-500/40 bg-sky-500/10",
  none: "text-zinc-500 border-zinc-700/40 bg-zinc-800/40",
}[s] || "text-zinc-500 border-zinc-700/40 bg-zinc-800/40");

const fmt = (t) => (t ? new Date(t).toLocaleString() : "—");

function Field({ label, value }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] uppercase tracking-wide text-zinc-500">{label}</span>
      <span className="text-sm text-zinc-200 break-all">{value ?? "—"}</span>
    </div>
  );
}

const errMsg = (e, fb) =>
  e?.response?.data?.detail?.message || e?.response?.data?.message ||
  e?.response?.data?.detail?.error || e?.response?.data?.detail || fb;

function RunList({ onSelect }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState("");
  const [moduleId, setModuleId] = useState("");
  const [filters, setFilters] = useState({ modules: [], statuses: [], projects: [] });

  useEffect(() => {
    axios.get(`${API}/canonical/filters`)
      .then(({ data }) => setFilters({
        modules: data.modules || [], statuses: data.statuses || [], projects: data.projects || [] }))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (status) params.status = status;
      if (moduleId) params.module_id = moduleId;
      const { data } = await axios.get(`${API}/canonical/runs`, { params });
      setRuns(data.runs || []);
    } catch (e) {
      setRuns([]);
      setError(errMsg(e, "Failed to load canonical runs."));
    } finally {
      setLoading(false);
    }
  }, [status, moduleId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="canonical-runs-list">
      {error && (
        <div data-testid="runs-error" className="mb-4 rounded-lg border border-rose-500/40 bg-rose-500/10 text-rose-300 px-4 py-3 text-sm">
          {error}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <select data-testid="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}
          aria-label="Filter runs by status"
          className="bg-zinc-900 border border-zinc-700 rounded-md text-sm text-zinc-200 px-3 py-2">
          <option value="">All statuses</option>
          {filters.statuses.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <select data-testid="filter-module" value={moduleId} onChange={(e) => setModuleId(e.target.value)}
          aria-label="Filter runs by module"
          className="bg-zinc-900 border border-zinc-700 rounded-md text-sm text-zinc-200 px-3 py-2">
          <option value="">All modules</option>
          {filters.modules.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <Button data-testid="refresh-runs-btn" variant="outline" size="sm" onClick={load}
          className="border-zinc-700 text-zinc-300">
          <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-zinc-400 py-16 justify-center">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading canonical runs…
        </div>
      ) : runs.length === 0 ? (
        <div data-testid="runs-empty" className="text-center text-zinc-500 py-16 border border-dashed border-zinc-800 rounded-xl">
          No canonical runs yet. Execute a HARVEST run to produce one.
        </div>
      ) : (
        <div className="overflow-x-auto border border-zinc-800 rounded-xl">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-zinc-500 border-b border-zinc-800 text-xs uppercase tracking-wide">
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Module</th>
                <th className="px-4 py-3">Task</th>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3">Evidence</th>
                <th className="px-4 py-3">Cost</th>
                <th className="px-4 py-3">Report</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.run_id} data-testid={`run-row-${r.run_id}`}
                  role="button" tabIndex={0}
                  aria-label={`Open run ${r.run_id}, module ${r.module_id}, status ${r.status}`}
                  onClick={() => onSelect(r.run_id)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(r.run_id); } }}
                  className="border-b border-zinc-900 hover:bg-zinc-900/60 focus:bg-zinc-900/80 focus:outline-none focus:ring-1 focus:ring-emerald-500/60 cursor-pointer transition-colors">
                  <td className="px-4 py-3"><Badge className={`${runTone(r.status)} border`}>{r.status}</Badge></td>
                  <td className="px-4 py-3 text-zinc-300">{r.module_id}</td>
                  <td className="px-4 py-3 text-zinc-400 font-mono text-xs">{r.task_id?.slice(0, 16)}</td>
                  <td className="px-4 py-3 text-zinc-400 text-xs">{fmt(r.created_at)}</td>
                  <td className="px-4 py-3"><Badge className={`${evTone(r.evidence_state)} border`} aria-label={`Evidence state ${r.evidence_state}`}>{r.evidence_state}</Badge></td>
                  <td className="px-4 py-3">{r.cost_available
                    ? <DollarSign className="w-4 h-4 text-emerald-400" role="img" aria-label="Cost available" />
                    : <span className="text-zinc-600 text-xs" aria-label="Cost not available">n/a</span>}</td>
                  <td className="px-4 py-3">{r.report_available
                    ? <FileText className="w-4 h-4 text-emerald-400" role="img" aria-label="Report available" />
                    : <span className="text-zinc-600 text-xs" aria-label="No report">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function RunDetail({ runId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/canonical/runs/${runId}`);
      setData(res.data);
    } catch (e) {
      setData(null);
      setError(e?.response?.status === 404
        ? "Run not found or access denied."
        : (e?.response?.data?.message || "Failed to reconstruct run."));
    } finally {
      setLoading(false);
    }
  }, [runId]);

  useEffect(() => { load(); }, [load]);

  const act = async (verb) => {
    setBusy(true);
    setError(null);
    try {
      await axios.post(`${API}/canonical/runs/${runId}/${verb}`);
      await load();
    } catch (e) {
      setError(errMsg(e, `Action '${verb}' failed.`));
    } finally { setBusy(false); }
  };

  if (loading) return (
    <div className="flex items-center gap-2 text-zinc-400 py-16 justify-center">
      <Loader2 className="w-5 h-5 animate-spin" /> Reconstructing run…
    </div>
  );
  if (!data) return (
    <div className="space-y-4">
      <Button data-testid="back-to-runs-btn" variant="outline" size="sm" onClick={onBack}
        className="border-zinc-700 text-zinc-300">
        <ArrowLeft className="w-4 h-4 mr-1.5" /> All runs
      </Button>
      <div data-testid="run-detail-error" className="text-center text-rose-300 py-16 border border-rose-500/30 rounded-xl">
        {error || "Run not found or access denied."}
      </div>
    </div>
  );

  const { run, contract, task, dependency_graph, work_units, events, evidence, artifacts, legacy_identifiers, integrity, sync_state } = data;
  const ident = contract?.identity || {};
  const canCancel = ["created", "authorized", "running", "cancellation_requested"].includes(run.status);
  const rerunSupported = run.rerun_supported === true;

  return (
    <div data-testid="canonical-run-detail" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Button data-testid="back-to-runs-btn" variant="outline" size="sm" onClick={onBack}
          className="border-zinc-700 text-zinc-300">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> All runs
        </Button>
        <div className="flex items-center gap-2">
          <Badge className={`${runTone(run.status)} border`}>{run.status}</Badge>
          {rerunSupported ? (
            <Button data-testid="rerun-btn" variant="outline" size="sm" disabled={busy}
              onClick={() => act("rerun")} className="border-zinc-700 text-zinc-300">
              <RefreshCw className="w-4 h-4 mr-1.5" /> Rerun
            </Button>
          ) : (
            <Button data-testid="rerun-btn" variant="outline" size="sm" disabled
              title="Rerun not supported for this module yet"
              aria-label="Rerun not supported" className="border-zinc-800 text-zinc-600 cursor-not-allowed">
              <RefreshCw className="w-4 h-4 mr-1.5" /> Rerun
            </Button>
          )}
          {canCancel && (
            <Button data-testid="cancel-btn" variant="outline" size="sm" disabled={busy}
              onClick={() => act("cancel")} className="border-rose-700 text-rose-300">
              <Ban className="w-4 h-4 mr-1.5" /> Cancel
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div data-testid="run-action-error" className="rounded-lg border border-rose-500/40 bg-rose-500/10 text-rose-300 px-4 py-3 text-sm">
          {error}
        </div>
      )}
      {sync_state === "synchronization_incomplete" && (
        <div data-testid="sync-incomplete-banner" className="rounded-lg border border-amber-500/40 bg-amber-500/10 text-amber-300 px-4 py-3 text-sm">
          Synchronization incomplete — this run is still being projected from the module engine. Refresh shortly.
        </div>
      )}
      {integrity && integrity.ok === false && (
        <div data-testid="integrity-banner" className="rounded-lg border border-rose-500/50 bg-rose-500/10 text-rose-300 px-4 py-3 text-sm">
          Integrity check failed — event chain {integrity.event_chain_verified ? "ok" : "BROKEN"};
          tampered evidence: {integrity.evidence_tampered?.length || 0},
          artifacts: {integrity.artifacts_tampered?.length || 0}.
        </div>
      )}
      {run.status === "cancellation_requested" && (
        <div data-testid="cancel-requested-banner" className="rounded-lg border border-amber-500/40 bg-amber-500/10 text-amber-300 px-4 py-3 text-sm">
          Cancellation requested — awaiting the module worker to confirm the terminal state.
        </div>
      )}

      {/* Immutable contract identity */}
      <section data-testid="run-contract" className="border border-zinc-800 rounded-xl p-5">
        <h3 className="flex items-center gap-2 text-zinc-200 font-semibold mb-4">
          <ShieldCheck className="w-4 h-4 text-emerald-400" /> Immutable Run Contract
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Field label="Run" value={run.run_id} />
          <Field label="Module" value={`${ident.module_id} v${ident.module_version ?? "—"}`} />
          <Field label="Purpose" value={ident.purpose} />
          <Field label="Attempt" value={run.attempt} />
          <Field label="Project" value={ident.project_id} />
          <Field label="Conversation" value={ident.conversation_id} />
          <Field label="Task" value={ident.task_id} />
          <Field label="Contract digest" value={run.contract_digest?.slice(0, 20) + "…"} />
          {run.rerun_of && <Field label="Rerun of" value={run.rerun_of} />}
          <Field label="Retention" value={run.retention_state} />
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Work units + dependency graph */}
        <section data-testid="run-work-units" className="border border-zinc-800 rounded-xl p-5">
          <h3 className="flex items-center gap-2 text-zinc-200 font-semibold mb-4">
            <ListTree className="w-4 h-4 text-sky-400" /> Work Units & Dependencies
            <span className="text-zinc-500 text-xs">({work_units?.length || 0})</span>
          </h3>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {(work_units || []).map((w) => (
              <div key={w.work_unit_id} className="flex items-center justify-between text-sm border-b border-zinc-900 pb-2">
                <span className="text-zinc-300">{w.capability || w.work_unit_id}</span>
                <div className="flex items-center gap-2">
                  <span className="text-zinc-600 text-xs">×{w.attempts}</span>
                  <Badge className={`${runTone(w.status === "completed" ? "completed" : w.status === "failed" ? "failed" : "running")} border text-xs`}>
                    {w.status}
                  </Badge>
                </div>
              </div>
            ))}
            {(!work_units || work_units.length === 0) && <p className="text-zinc-600 text-sm">No work units.</p>}
          </div>
          <p className="text-xs text-zinc-600 mt-3">
            <GitBranch className="w-3 h-3 inline mr-1" />
            {dependency_graph?.nodes?.length || 0} nodes · {dependency_graph?.edges?.length || 0} edges
          </p>
        </section>

        {/* Ordered event timeline */}
        <section data-testid="run-events" className="border border-zinc-800 rounded-xl p-5">
          <h3 className="flex items-center gap-2 text-zinc-200 font-semibold mb-4">
            <Activity className="w-4 h-4 text-indigo-400" /> Ordered Run Events
            <span className="text-zinc-500 text-xs">({events?.length || 0})</span>
          </h3>
          <div className="space-y-1.5 max-h-72 overflow-y-auto font-mono text-xs">
            {(events || []).map((e) => (
              <div key={e.event_id} className="flex items-center gap-3">
                <span className="text-zinc-600 w-8 text-right">{e.sequence}</span>
                <span className="text-emerald-400">{e.event_type}</span>
                <span className="text-zinc-600 truncate">{e.work_unit_id || ""}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Evidence */}
        <section data-testid="run-evidence" className="border border-zinc-800 rounded-xl p-5">
          <h3 className="flex items-center gap-2 text-zinc-200 font-semibold mb-4">
            <ShieldCheck className="w-4 h-4 text-emerald-400" /> Evidence
            <span className="text-zinc-500 text-xs">({evidence?.length || 0})</span>
          </h3>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {(evidence || []).map((ev) => (
              <div key={ev.evidence_id} className="flex items-center justify-between text-sm border-b border-zinc-900 pb-2">
                <span className="text-zinc-300">{ev.source_entity}</span>
                <Badge className={`${evTone(ev.status === "verified" ? "verified" : "partial")} border text-xs`}>{ev.status}</Badge>
              </div>
            ))}
            {(!evidence || evidence.length === 0) && <p className="text-zinc-600 text-sm">No evidence recorded.</p>}
          </div>
        </section>

        {/* Artifacts / Reports + legacy identifiers */}
        <section data-testid="run-artifacts" className="border border-zinc-800 rounded-xl p-5">
          <h3 className="flex items-center gap-2 text-zinc-200 font-semibold mb-4">
            <Boxes className="w-4 h-4 text-amber-400" /> Artifacts & Reports
            <span className="text-zinc-500 text-xs">({artifacts?.length || 0})</span>
          </h3>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {(artifacts || []).map((a) => (
              <div key={a.artifact_id} className="flex items-center justify-between text-sm border-b border-zinc-900 pb-2">
                <span className="text-zinc-300 flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5" />{a.report_key || a.kind} v{a.version}
                </span>
                <Badge className="border border-zinc-700 text-zinc-400 text-xs">{a.classification}</Badge>
              </div>
            ))}
            {(!artifacts || artifacts.length === 0) && <p className="text-zinc-600 text-sm">No artifacts.</p>}
          </div>
          <div className="mt-4 pt-3 border-t border-zinc-900">
            <span className="text-[11px] uppercase tracking-wide text-zinc-500 flex items-center gap-1.5 mb-2">
              <Layers className="w-3 h-3" /> Legacy identifiers
            </span>
            <div className="flex flex-wrap gap-1.5">
              {(legacy_identifiers || []).map((l, i) => (
                <Badge key={i} className="border border-zinc-700 text-zinc-400 text-xs font-mono">
                  {l.legacy_id} → {l.registry_id || l.path}
                </Badge>
              ))}
              {(!legacy_identifiers || legacy_identifiers.length === 0) && <span className="text-zinc-600 text-xs">none</span>}
            </div>
            {run.legacy_execution_id && (
              <p className="text-xs text-zinc-600 mt-2">
                <Clock className="w-3 h-3 inline mr-1" /> Legacy execution: {run.legacy_execution_id}
              </p>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

export default function CanonicalRuns() {
  const navigate = useNavigate();
  const { runId } = useParams();

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
              <Activity className="w-7 h-7 text-emerald-400" /> Run History
            </h1>
            <p className="text-zinc-500 mt-1 text-sm">
              Canonical, module-neutral execution history — one API reconstructs each entire run.
            </p>
          </div>
          <Button data-testid="dashboard-btn" variant="outline" onClick={() => navigate("/dashboard")}
            className="border-zinc-700 text-zinc-300">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Dashboard
          </Button>
        </div>

        {runId
          ? <RunDetail runId={runId} onBack={() => navigate("/runs")} />
          : <RunList onSelect={(id) => navigate(`/runs/${id}`)} />}
      </div>
    </div>
  );
}
